import { Link } from 'react-router-dom'
import { CreditCard, Receipt, BarChart3, ArrowRight, CheckCircle, Clock, AlertCircle } from 'lucide-react'
import { useSubscription, useUsage, useUserProfile, formatPrice } from '../../hooks/useBilling'

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    active: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-700 dark:text-emerald-300', label: 'Actif' },
    trialing: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', label: 'Essai' },
    past_due: { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', label: 'Impayé' },
    canceled: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', label: 'Annulé' },
  }
  const c = config[status] || config.active
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${c.bg} ${c.text}`}>
      {status === 'active' && <CheckCircle className="h-3 w-3" />}
      {status === 'trialing' && <Clock className="h-3 w-3" />}
      {status === 'canceled' && <AlertCircle className="h-3 w-3" />}
      {c.label}
    </span>
  )
}

function UsageMiniBar({ current, limit, isUnlimited }: { current: number; limit: number; isUnlimited: boolean }) {
  if (isUnlimited) {
    return <span className="text-xs text-zinc-500">∞ Illimité</span>
  }
  const percent = Math.min(100, (current / limit) * 100)
  const color = percent > 80 ? 'bg-red-500' : percent > 50 ? 'bg-amber-500' : 'bg-emerald-500'
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
        <div className={`h-full ${color} transition-all`} style={{ width: `${percent}%` }} />
      </div>
      <span className="text-xs text-zinc-500">{current}/{limit}</span>
    </div>
  )
}

export default function AccountOverview() {
  const { profile, loading: profileLoading } = useUserProfile()
  const { subscription, hasSubscription, loading: subLoading, daysUntilRenewal } = useSubscription()
  const { usage, loading: usageLoading } = useUsage()

  const loading = profileLoading || subLoading || usageLoading

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    )
  }

  const initials = profile 
    ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase() || profile.email[0].toUpperCase()
    : '?'

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-2xl font-bold text-white shadow-lg shadow-emerald-500/30">
          {initials}
        </div>
        <div>
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
            {profile?.first_name && profile?.last_name 
              ? `${profile.first_name} ${profile.last_name}`
              : profile?.email}
          </h2>
          <p className="text-sm text-zinc-500">{profile?.email}</p>
          {profile?.is_verified && (
            <span className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle className="h-3 w-3" /> Compte vérifié
            </span>
          )}
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Subscription Card */}
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 dark:border-zinc-700 dark:bg-zinc-800/50">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-500" />
              <h3 className="font-semibold text-zinc-900 dark:text-white">Abonnement</h3>
            </div>
            {hasSubscription && subscription && <StatusBadge status={subscription.status} />}
          </div>
          
          {hasSubscription && subscription ? (
            <div className="space-y-2">
              <p className="text-lg font-medium text-zinc-900 dark:text-white">{subscription.plan_name}</p>
              <p className="text-sm text-zinc-500">
                {subscription.billing_interval === 'year' ? 'Facturation annuelle' : 'Facturation mensuelle'}
              </p>
              {daysUntilRenewal !== null && (
                <p className="text-sm text-zinc-500">
                  {subscription.cancel_at_period_end 
                    ? `Accès jusqu'au ${new Date(subscription.current_period_end!).toLocaleDateString('fr-FR')}`
                    : `Prochain renouvellement dans ${daysUntilRenewal} jours`}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-zinc-500">Aucun abonnement actif</p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
              >
                Voir les offres <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
          
          <Link
            to="/account/subscription"
            className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
          >
            Gérer l'abonnement <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Usage Card */}
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 dark:border-zinc-700 dark:bg-zinc-800/50">
          <div className="mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-emerald-500" />
            <h3 className="font-semibold text-zinc-900 dark:text-white">Utilisation</h3>
          </div>
          
          {usage ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">Agents</span>
                <UsageMiniBar 
                  current={usage.agents.current} 
                  limit={usage.agents.limit} 
                  isUnlimited={usage.agents.is_unlimited} 
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">Conversations</span>
                <UsageMiniBar 
                  current={usage.conversations.current} 
                  limit={usage.conversations.limit} 
                  isUnlimited={usage.conversations.is_unlimited} 
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">Documents</span>
                <UsageMiniBar 
                  current={usage.knowledge_items.current} 
                  limit={usage.knowledge_items.limit} 
                  isUnlimited={usage.knowledge_items.is_unlimited} 
                />
              </div>
            </div>
          ) : (
            <p className="text-zinc-500">Chargement...</p>
          )}
          
          <Link
            to="/account/usage"
            className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
          >
            Voir les détails <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Link
          to="/pricing"
          className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
        >
          {hasSubscription ? 'Changer de plan' : 'Voir les offres'}
        </Link>
        <Link
          to="/account/billing"
          className="rounded-full border border-zinc-300 bg-white px-5 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
        >
          <Receipt className="mr-2 inline h-4 w-4" />
          Voir les factures
        </Link>
        <Link
          to="/account/settings"
          className="rounded-full border border-zinc-300 bg-white px-5 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
        >
          Paramètres
        </Link>
      </div>
    </div>
  )
}
